/**
 * Corrección de datos de un equipo.
 *
 * Pantalla aparte de la de alta, y no un «modo edición» de aquella, porque
 * lo que se puede corregir NO es lo que se da de alta:
 *
 * - **La ubicación no se toca aquí.** Se cambia con «mover», que deja
 *   rastro de dónde estaba y dónde está. Editarla a mano borraría el
 *   histórico de traslados, que es la trazabilidad que promete el pasaporte.
 * - **El tipo tampoco.** Sus obligaciones salen del tipo: cambiarlo dejaría
 *   un plan calculado sobre reglas que ya no aplican. Si el tipo estaba mal,
 *   el equipo estaba mal dado de alta.
 *
 * Lo que sí se corrige son los datos que se teclean mal o llegan
 * incompletos de una importación: número de serie, lote, modelo y las
 * fechas de ciclo de vida.
 *
 * ⚠️ Las fechas cambian los vencimientos. El timbrado y la puesta en
 * servicio son la base del plan, así que tras corregirlas hay que
 * regenerarlo — la pantalla lo recuerda al guardar.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { useForm } from '../../forms/use-form.js';
import { CheckField, FormSection, SelectField, TextArea, TextField } from '../../forms/fields.js';
import { isoDate, maxLength, notInFuture, required, type FieldRules } from '../../forms/form-state.js';

interface Editable {
  id: string;
  internal_code: string;
  serial_number: string | null;
  lot: string | null;
  equipment_model_id: string | null;
  manufacturing_date: string | null;
  installation_date: string | null;
  commissioning_date: string | null;
  stamping_date: string | null;
  restamping_count: number;
  manufacturer_life_years: number | null;
  certified_durability_years: number | null;
  last_verification_date: string | null;
  contains_fluorinated_gas: boolean;
  is_contracted: boolean;
  notes: string | null;
  equipment_type_code: string;
  equipment_type_name: string;
  site_name: string;
  status: string;
}

interface Model {
  id: string;
  code: string;
  name: string;
  manufacturer_name: string | null;
  equipment_type_code: string;
}

const TODAY = new Date().toISOString().slice(0, 10);

const RULES: FieldRules = {
  internalCode: [required('El código interno'), maxLength(32)],
  serialNumber: [maxLength(64)],
  installationDate: [isoDate(), notInFuture(TODAY)],
  commissioningDate: [isoDate(), notInFuture(TODAY)],
  manufacturingDate: [isoDate(), notInFuture(TODAY)],
  stampingDate: [isoDate(), notInFuture(TODAY)],
  lastVerificationDate: [isoDate(), notInFuture(TODAY)],
};

const NULLABLE = [
  'serialNumber',
  'lot',
  'equipmentModelId',
  'manufacturingDate',
  'installationDate',
  'commissioningDate',
  'stampingDate',
  'lastVerificationDate',
  'notes',
];

export function EquipmentEditScreen(): React.ReactNode {
  const { id } = useParams();
  const { api } = useSession();
  const navigate = useNavigate();

  const [current, setCurrent] = useState<Editable | null>(null);
  const [models, setModels] = useState<Model[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    const [row, mods] = await Promise.all([
      api.get<Editable>(`/equipment/${id}/editable`),
      api.get<{ items: Model[] }>('/catalog/models').catch(() => ({ items: [] as Model[] })),
    ]);
    setCurrent(row);
    setModels(mods.items);
  }, [api, id]);

  useEffect(() => {
    load().catch((caught: unknown) => {
      setLoadError(
        caught instanceof ApiError ? caught.message : 'No se ha podido cargar el equipo.',
      );
    });
  }, [load]);

  const form = useForm<void>({
    mode: 'edit',
    rules: RULES,
    nullable: NULLABLE,
    initial: {
      internalCode: current?.internal_code ?? '',
      serialNumber: current?.serial_number ?? '',
      lot: current?.lot ?? '',
      equipmentModelId: current?.equipment_model_id ?? '',
      manufacturingDate: current?.manufacturing_date ?? '',
      installationDate: current?.installation_date ?? '',
      commissioningDate: current?.commissioning_date ?? '',
      stampingDate: current?.stamping_date ?? '',
      restampingCount: current?.restamping_count ?? 0,
      manufacturerLifeYears: current?.manufacturer_life_years ?? '',
      certifiedDurabilityYears: current?.certified_durability_years ?? '',
      lastVerificationDate: current?.last_verification_date ?? '',
      containsFluorinatedGas: current?.contains_fluorinated_gas ?? false,
      isContracted: current?.is_contracted ?? true,
      notes: current?.notes ?? '',
    },
    onSubmit: async (body, client) => {
      const payload: Record<string, unknown> = { ...body };
      // Los numéricos viajan como número; el formulario los tiene como texto.
      for (const key of ['restampingCount', 'manufacturerLifeYears', 'certifiedDurabilityYears']) {
        const value = payload[key];
        if (value === '' || value === null) delete payload[key];
        else if (value !== undefined) payload[key] = Number(value);
      }

      return client.request<void>(`/equipment/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
    },
    onSaved: () => {
      setSaved(true);
      void load();
    },
  });

  if (loadError !== null) {
    return (
      <section className="screen">
        <p className="screen__error" role="alert">
          {loadError}
        </p>
      </section>
    );
  }

  if (current === null) return <p className="screen__note">Cargando…</p>;

  // Sólo los modelos de su tipo: asignarle el modelo de otro tipo de equipo
  // sería un dato incoherente que además acabaría impreso en el acta.
  const usableModels = models.filter((m) => m.equipment_type_code === current.equipment_type_code);

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">{current.internal_code}</h1>
        <span className="screen__count">
          {current.equipment_type_name} · {current.site_name}
        </span>
      </header>

      <p className="screen__note">
        El <strong>centro y la ubicación</strong> no se cambian aquí: para eso está «mover», que
        deja rastro del traslado. El <strong>tipo de equipo</strong> tampoco, porque de él salen sus
        obligaciones.{' '}
        <Link to={`/equipos/${current.id}`}>Ver el pasaporte</Link>
      </p>

      {saved && (
        <p className="screen__note screen__note--strong" role="status">
          Guardado. Si ha cambiado el timbrado o la puesta en servicio, regenere el plan del
          contrato: son la base de los vencimientos.
        </p>
      )}

      <form className="form" onSubmit={(e) => void form.submit(e)} noValidate>
        {form.formError !== null && (
          <p className="screen__error" role="alert">
            {form.formError}
          </p>
        )}

        <FormSection title="Identificación">
          <TextField
            label="Código interno"
            value={String(form.values['internalCode'] ?? '')}
            onChange={(v) => form.set('internalCode', v)}
            onBlur={() => form.blur('internalCode')}
            error={form.errors['internalCode']}
            required
          />
          <TextField
            label="Número de serie"
            value={String(form.values['serialNumber'] ?? '')}
            onChange={(v) => form.set('serialNumber', v)}
            onBlur={() => form.blur('serialNumber')}
            error={form.errors['serialNumber']}
            help="Identificación unívoca que declara el acta (anexo II §5.b)."
          />
          <TextField
            label="Lote"
            value={String(form.values['lot'] ?? '')}
            onChange={(v) => form.set('lot', v)}
          />
          <SelectField
            label="Modelo"
            value={String(form.values['equipmentModelId'] ?? '')}
            onChange={(v) => form.set('equipmentModelId', v)}
            options={usableModels.map((m) => ({
              value: m.id,
              label: m.manufacturer_name ? `${m.manufacturer_name} · ${m.name}` : m.name,
            }))}
            emptyLabel="Sin modelo asignado"
            help={
              usableModels.length === 0
                ? 'No hay modelos de este tipo. Créelos en Activos → Catálogo: el acta declara marca y modelo.'
                : 'El acta declara marca y modelo de cada equipo (anexo II §5.b).'
            }
          />
        </FormSection>

        <FormSection title="Ciclo de vida">
          <TextField
            label="Fabricación"
            type="date"
            value={String(form.values['manufacturingDate'] ?? '')}
            onChange={(v) => form.set('manufacturingDate', v)}
            onBlur={() => form.blur('manufacturingDate')}
            error={form.errors['manufacturingDate']}
          />
          <TextField
            label="Instalación"
            type="date"
            value={String(form.values['installationDate'] ?? '')}
            onChange={(v) => form.set('installationDate', v)}
            onBlur={() => form.blur('installationDate')}
            error={form.errors['installationDate']}
          />
          <TextField
            label="Puesta en servicio"
            type="date"
            value={String(form.values['commissioningDate'] ?? '')}
            onChange={(v) => form.set('commissioningDate', v)}
            onBlur={() => form.blur('commissioningDate')}
            error={form.errors['commissioningDate']}
            help="Base del cálculo de vencimientos mientras no haya mantenimiento previo."
          />
          <TextField
            label="Timbrado"
            type="date"
            value={String(form.values['stampingDate'] ?? '')}
            onChange={(v) => form.set('stampingDate', v)}
            onBlur={() => form.blur('stampingDate')}
            error={form.errors['stampingDate']}
            help="No es una caducidad: es el punto de partida de hasta tres retimbrados."
          />
          <TextField
            label="Retimbrados realizados"
            type="number"
            value={String(form.values['restampingCount'] ?? 0)}
            onChange={(v) => form.set('restampingCount', v)}
            help="Máximo tres. Al agotarlos, procede sustitución."
          />
          <TextField
            label="Última verificación"
            type="date"
            value={String(form.values['lastVerificationDate'] ?? '')}
            onChange={(v) => form.set('lastVerificationDate', v)}
            onBlur={() => form.blur('lastVerificationDate')}
            error={form.errors['lastVerificationDate']}
          />
        </FormSection>

        <FormSection title="Otros">
          <TextField
            label="Vida útil del fabricante (años)"
            type="number"
            value={String(form.values['manufacturerLifeYears'] ?? '')}
            onChange={(v) => form.set('manufacturerLifeYears', v)}
          />
          <TextField
            label="Durabilidad certificada (años)"
            type="number"
            value={String(form.values['certifiedDurabilityYears'] ?? '')}
            onChange={(v) => form.set('certifiedDurabilityYears', v)}
          />
          <CheckField
            label="Contiene gas fluorado"
            checked={form.values['containsFluorinatedGas'] === true}
            onChange={(on) => form.set('containsFluorinatedGas', on)}
          />
          <CheckField
            label="Incluido en contrato"
            checked={form.values['isContracted'] === true}
            onChange={(on) => form.set('isContracted', on)}
            help="Un equipo fuera de contrato no genera obligaciones de mantenimiento."
          />
          <TextArea
            label="Notas"
            value={String(form.values['notes'] ?? '')}
            onChange={(v) => form.set('notes', v)}
          />
        </FormSection>

        <footer className="form__footer">
          <button className="button button--quiet" type="button" onClick={() => navigate(-1)}>
            Cancelar
          </button>
          <button className="button button--primary" type="submit" disabled={form.saving}>
            {form.saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </footer>
      </form>
    </section>
  );
}
